import { faSwap } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';

import type { Visit } from '@/@types/visits';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

interface ComparisonModeToggleProps {
  section: 'left' | 'right';
  isPhotoMode: boolean;
  visits: Visit[];
  selectedVisit: number;
  onToggleMode: (isPhotoMode: boolean) => void;
  onVisitChange: (visitId: number) => void;
}

export const ComparisonModeToggle = ({
  section,
  isPhotoMode,
  visits,
  selectedVisit,
  onToggleMode,
  onVisitChange,
}: ComparisonModeToggleProps) => {
  const { trackEvent } = useTrackEvent();

  const getVisitHasColorImages = (visit: Visit): boolean => {
    if (!visit.imageTypeCounts) return false;

    const colorImageCount = visit.imageTypeCounts?.find(
      (count) => count.type === 'COLOR_IMAGE',
    );
    return Boolean(colorImageCount && Number(colorImageCount.count) > 0);
  };

  // Find the most recent visit with color images
  const findMostRecentVisitWithColorImages = () => {
    if (!visits.length) return null;

    // Sort visits by date (most recent first)
    const sortedVisits = [...visits].sort((a, b) => {
      // If both dates are null/undefined, maintain original order
      if (!a.visitDate && !b.visitDate) return 0;

      // If only one date is null/undefined, place the one without date at the end
      if (!a.visitDate) return 1;
      if (!b.visitDate) return -1;

      // Both dates exist, sort by most recent first
      return new Date(b.visitDate).getTime() - new Date(a.visitDate).getTime();
    });

    // Find the first visit that has color images
    return sortedVisits.find(getVisitHasColorImages);
  };

  // Check if any visits have color images
  const hasVisitsWithColorImages = visits.some(getVisitHasColorImages);

  const handleToggleComparisonMode = () => {
    const currentMode = isPhotoMode ? 'photo' : 'xray';

    if (isPhotoMode) {
      // Switching back to x-ray mode - reset to default visit selection
      onToggleMode(false);
      if (visits.length > 1 && section === 'left' && visits[1]?.id) {
        onVisitChange(visits[1].id || -1);
      } else if (visits[0]?.id) {
        onVisitChange(visits[0].id || -1);
      }
    } else {
      // Switching to photo mode - find most recent visit with color images
      onToggleMode(true);
      const mostRecentPhotoVisit = findMostRecentVisitWithColorImages();
      if (mostRecentPhotoVisit?.id) {
        onVisitChange(mostRecentPhotoVisit.id);
      }
    }

    // Track the mode change
    trackEvent(mixpanelEvents.compareImageSelect, {
      section,
      visitId: selectedVisit,
      mode: currentMode === 'photo' ? 'xray' : 'photo',
      action: 'toggle_comparison_mode',
    });
  };

  // Only show in left section and when there are visits with color images
  if (section !== 'left' || !hasVisitsWithColorImages) {
    return null;
  }

  return (
    <Button
      data-testid="comparison-mode-toggle"
      onClick={handleToggleComparisonMode}
      variant="outline"
      size="sm"
      className="vai-bg-sapphire-900 hover:vai-bg-sapphire-800 vai-border-[#2368F6] hover:vai-border-[#2368F6] vai-text-[#FAFAFA] hover:vai-text-white vai-font-medium vai-px-4 vai-py-2 vai-rounded-md vai-transition-colors vai-flex vai-items-center vai-gap-2"
    >
      <FontAwesomeIcon icon={faSwap} className="vai-text-sm" />
      {isPhotoMode ? (
        <Trans>Compare to historical x-ray</Trans>
      ) : (
        <Trans>Compare to latest photo</Trans>
      )}
    </Button>
  );
};
